import React from 'react';
import { LayoutDashboard, Briefcase, Search, Workflow, Stethoscope, PenTool, CheckSquare, Settings, Scale, ArrowLeftRight } from 'lucide-react';
import { PersonaId } from '../../App';
interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  persona: PersonaId | null;
}
export function Sidebar({
  currentPage,
  onNavigate,
  persona
}: SidebarProps) {
  const allNavItems = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard
  }, {
    id: 'matters',
    label: 'Matters',
    icon: Briefcase
  }, {
    id: 'search',
    label: 'Firm Knowledge Search',
    icon: Search
  }, {
    id: 'workflows',
    label: 'Matter Intake & Organization',
    icon: Workflow
  }, {
    id: 'chronology',
    label: 'Medical Timeline',
    icon: Stethoscope
  }, {
    id: 'copilot',
    label: 'Draft Assistant',
    icon: PenTool
  }, {
    id: 'review',
    label: 'Attorney Tasks',
    icon: CheckSquare
  }, {
    id: 'admin',
    label: 'Administration',
    icon: Settings
  }];

  // Role-based nav access
  const getAccessiblePages = (): string[] => {
    switch (persona) {
      case 'EF':
        // Managing Attorney — full access
        return ['dashboard', 'matters', 'search', 'workflows', 'chronology', 'copilot', 'review', 'admin'];
      case 'AT':
        // Paralegal Reviewer
        return ['dashboard', 'matters', 'search', 'chronology', 'review'];
      case 'MS':
        // Intake Coordinator
        return ['dashboard', 'matters', 'search', 'workflows'];
      case 'RK':
        // Litigation Support
        return ['dashboard', 'search', 'workflows', 'admin'];
      default:
        return ['dashboard', 'matters', 'search', 'workflows', 'chronology', 'copilot', 'review', 'admin'];
    }
  };
  const accessiblePages = getAccessiblePages();
  const navItems = allNavItems.filter(item => accessiblePages.includes(item.id));
  const getPersonaDetails = () => {
    switch (persona) {
      case 'EF':
        return {
          name: 'Edward Festeryga',
          role: 'Managing Attorney',
          initials: 'EF',
          badgeLabel: 'Full Access',
          badgeColor: 'bg-red-900/50 text-red-300'
        };
      case 'AT':
        return {
          name: 'Ana Torres',
          role: 'Paralegal Reviewer',
          initials: 'AT',
          badgeLabel: 'Reviewer',
          badgeColor: 'bg-blue-900/50 text-blue-300'
        };
      case 'MS':
        return {
          name: 'Maria Santos',
          role: 'Intake Coordinator',
          initials: 'MS',
          badgeLabel: 'Intake',
          badgeColor: 'bg-emerald-900/50 text-emerald-300'
        };
      case 'RK':
        return {
          name: 'Robert Kim',
          role: 'Litigation Support',
          initials: 'RK',
          badgeLabel: 'Lit Support',
          badgeColor: 'bg-purple-900/50 text-purple-300'
        };
      default:
        return {
          name: 'Sarah Jenkins',
          role: 'Managing Partner',
          initials: 'SJ',
          badgeLabel: 'Full Access',
          badgeColor: 'bg-red-900/50 text-red-300'
        };
    }
  };
  const user = getPersonaDetails();
  return <div className="w-64 bg-slate-900 text-slate-300 flex flex-col h-full border-r border-slate-800" data-id="element-75">
      <div className="p-6 flex items-center space-x-3 border-b border-slate-800" data-id="element-76">
        <div className="bg-accent p-1.5 rounded-md" data-id="element-77">
          <Scale className="w-5 h-5 text-white" data-id="element-78" />
        </div>
        <div data-id="element-79">
          <h1 className="text-sm font-bold text-white leading-tight" data-id="element-80">
            Festeryga Law
          </h1>
          <p className="text-[10px] text-slate-400 uppercase tracking-wider" data-id="element-81">
            Intelligence Platform
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4" data-id="element-82">
        <nav className="space-y-1 px-3" data-id="element-83">
          {navItems.map(item => {
          const Icon = item.icon;
          const isActive = currentPage === item.id || currentPage === 'workspace' && item.id === 'matters';
          return <button key={item.id} onClick={() => onNavigate(item.id)} className={`w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`} data-id="element-84">

                <Icon className={`w-4 h-4 ${isActive ? 'text-accent' : 'text-slate-500'}`} data-id="element-85" />

                <span data-id="element-86">{item.label}</span>
                {item.id === 'review' && <span className="ml-auto bg-accent text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-87">
                    3
                  </span>}
              </button>;
        })}
        </nav>
      </div>

      <div className="p-4 border-t border-slate-800 space-y-3" data-id="element-88">
        <div className="flex items-center space-x-3" data-id="element-89">
          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-medium text-white shrink-0" data-id="element-90">
            {user.initials}
          </div>
          <div className="flex-1 min-w-0" data-id="element-91">
            <p className="text-sm font-medium text-white truncate" data-id="element-92">
              {user.name}
            </p>
            <div className="flex items-center gap-2 mt-0.5" data-id="element-93">
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${user.badgeColor}`} data-id="element-94">

                {user.badgeLabel}
              </span>
            </div>
          </div>
        </div>
        <button onClick={() => onNavigate('login')} className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-xs font-medium text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 rounded-md transition-colors" data-id="element-95">

          <ArrowLeftRight className="w-3.5 h-3.5" data-id="element-96" />
          <span data-id="element-97">Switch Persona</span>
        </button>
      </div>
    </div>;
}